import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Sun, Moon, ArrowRight } from "lucide-react";
import { Logo } from "./Logo";
import { useTheme } from "../context/ThemeContext";
import { Button } from "./ui/button";
import { useModals } from "./modals/ModalProvider";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/solutions", label: "Solutions" },
  { to: "/cyber-risk-navigator", label: "Cyber Risk Navigator™" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const { theme, toggle } = useTheme();
  const { openBookConsultation } = useModals();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`print-hide fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "backdrop-blur-xl bg-background/75 border-b border-border" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-32 sm:h-36 flex items-center justify-between gap-4">
        <Link to="/" data-testid="navbar-logo-link" className="flex items-center">
          <Logo />
        </Link>
        <nav className="hidden lg:flex items-center gap-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/-+$/,"")}-link`}
              className={({ isActive }) =>
                `px-3 py-2 rounded-full text-[13px] font-semibold whitespace-nowrap transition-colors ${
                  isActive
                    ? "bg-primary/10 text-primary"
                    : "text-foreground/75 hover:text-primary hover:bg-primary/5"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          <button
            data-testid="theme-toggle-btn"
            onClick={toggle}
            aria-label="Toggle theme"
            className="h-10 w-10 grid place-items-center rounded-full border border-border hover:border-primary/50 transition"
          >
            {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>
          <Button
            data-testid="navbar-book-consultation-btn"
            onClick={openBookConsultation}
            className="hidden sm:inline-flex bg-primary hover:bg-primary/90 text-primary-foreground rounded-full px-5"
          >
            Book Consultation <ArrowRight className="ml-1 h-4 w-4" />
          </Button>
          <button
            className="lg:hidden h-10 w-10 grid place-items-center rounded-full border border-border"
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>
      {open && (
        <div className="lg:hidden border-t border-border bg-background/95 backdrop-blur-xl">
          <div className="px-4 py-4 flex flex-col gap-1">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                data-testid={`mobile-nav-${n.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/-+$/,"")}-link`}
                className={({ isActive }) =>
                  `px-3 py-3 rounded-lg text-sm font-semibold ${
                    isActive ? "bg-primary/10 text-primary" : "text-foreground/80"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
            <Button
              data-testid="mobile-book-consultation-btn"
              onClick={openBookConsultation}
              className="mt-2 bg-primary hover:bg-primary/90 rounded-full"
            >
              Book Consultation
            </Button>
          </div>
        </div>
      )}
    </header>
  );
}
